<template>
	<div class="sign-up-container">
		<InputTypeText
			v-model="form.first_name"
			:label="$t('form.label_first_name')"
			:required="true"
			:placeholder="$t('form.placeholder_first_name')"
			:error="errors.first_name.text"
		/>
		<InputTypeText
			v-model="form.email"
			:label="$t('form.label_email_pro')"
			:required="true"
			:placeholder="$t('form.placeholder_email_pro')"
			:error="errors.email.text"
		/>
		<InputTypePassword
			v-model="form.password"
			:label="$t('form.label_password')"
			:required="true"
			:placeholder="$t('form.placeholder_password')"
			:error="!errors.password.valid ? errors.password.text : ''"
			@update:model-value="checkPasswordSecurity"
		/>
		<div class="container-security-rules">
			<p class="security-rules">
				<span :class="{ 'rule-valid': passRules.size }">
					<img v-if="passRules.size" src="@/assets/icons/s-green-tick.svg?url" />
					<img v-else src="@/assets/icons/grey-cross.svg?url" />
					{{ $t('signup.password_security_char') }}
				</span>
				<span :class="{ 'rule-valid': passRules.numberLetter }">
					<img v-if="passRules.numberLetter" src="@/assets/icons/s-green-tick.svg?url" />
					<img v-else src="@/assets/icons/grey-cross.svg?url" />
					{{ $t('signup.password_security_number') }}
				</span>
			</p>
			<p class="security-rules">
				<span :class="{ 'rule-valid': passRules.case }">
					<img v-if="passRules.case" src="@/assets/icons/s-green-tick.svg?url" />
					<img v-else src="@/assets/icons/grey-cross.svg?url" />
					{{ $t('signup.password_security_maj') }}
				</span>
				<span :class="{ 'rule-valid': passRules.specialChar }">
					<img v-if="passRules.specialChar" src="@/assets/icons/s-green-tick.svg?url" />
					<img v-else src="@/assets/icons/grey-cross.svg?url" />
					{{ $t('signup.password_security_special_char') }}
				</span>
			</p>
		</div>
		<div class="checkbox-container">
			<input
				id="checkbox"
				ref="checkbox"
				v-model="form.cgu"
				type="checkbox"
				:class="{ 'field-error': !errors.cgu.valid }"
			/>
			<label for="checkbox">
				{{ $t('signup.cgu_part1') }}
				<a
					href="https://www.loyoly.io/fr/terms-and-conditions"
					target="_blank"
					class="cgv-cgu-link"
				>
					{{ $t('signup.cgu_part2') }}
				</a>
				{{ $t('signup.cgu_part3') }}
				<a
					href="https://www.loyoly.io/fr/terms-and-conditions"
					target="_blank"
					class="cgv-cgu-link"
				>
					{{ $t('signup.cgu_part4') }}
				</a>
			</label>
		</div>
		<div v-show="!errors.cgu.valid" class="container-error">
			<span class="error-message">
				{{ errors.cgu.text }}
			</span>
		</div>
		<DsButtonPrimary class="submit" :loading="isLoading" @click="submitForm">
			{{ $t('signup.create_account') }}
		</DsButtonPrimary>
		<span class="allready-account">
			{{ $t('signup.already_account') }}
			<nuxt-link to="/login" class="login-link">{{ $t('signup.connect') }}</nuxt-link>
		</span>
	</div>
</template>

<script>
import InputTypeText from '@/components/input/input-type-text'
import InputTypePassword from '@/components/input/input-type-password'

export default {
	name: 'SignUpForm',

	components: {
		InputTypeText,
		InputTypePassword,
	},
	emits: ['signUpMobile'],

	data() {
		return {
			isLoading: false,
			form: {
				first_name: '',
				email: '',
				password: '',
				cgu: false,
			},
			errors: {
				first_name: { valid: true, text: '' },
				email: { valid: true, text: '' },
				password: { valid: true, text: '' },
				cgu: { valid: true, text: '' },
			},
			passRules: {
				size: false,
				case: false,
				numberLetter: false,
				specialChar: false,
			},
		}
	},

	methods: {
		checkPasswordSecurity(inputValue) {
			this.passRules.case = inputValue.match(/[a-z]+/) && inputValue.match(/[A-Z]+/)
			this.passRules.numberLetter =
				inputValue.match(/[0-9]+/) &&
				(inputValue.match(/[a-z]+/) || inputValue.match(/[A-Z]+/))
			this.passRules.specialChar = inputValue.match(/[^A-Za-z0-9]+/)
			this.passRules.size = inputValue.length >= 8
		},

		checkEmail() {
			this.errors.email = { valid: true, text: '' }
			const re =
				/^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
			if (!re.test(this.form.email)) {
				this.errors.email = {
					valid: false,
					text: this.$t('form_error.email_not_valid'),
				}
			}
			if (
				[
					'gmail.com',
					'yahoo.com',
					'yahoo.fr',
					'hotmail.com',
					'hotmail.fr',
					'trustt.io',
					'sfr.fr',
					'free.fr',
					'orange.fr',
					'outlook.fr',
					'outlook.com',
					'msn.com',
					'live.fr',
					'laposte.fr',
					'aol.com',
					'yopmail.com',
					'protonmail.com',
					'icloud.com',
				].includes(this.form.email.substring(this.form.email.indexOf('@') + 1))
			) {
				this.errors.email = {
					valid: false,
					text: this.$t('form_error.email_pro_not_valid'),
				}
			}
		},

		checkPassword() {
			this.errors.password = { valid: true, text: '' }
			this.checkPasswordSecurity(this.form.password)
			if (
				!this.passRules.size ||
				!this.passRules.case ||
				!this.passRules.numberLetter ||
				!this.passRules.specialChar
			) {
				this.errors.password = {
					valid: false,
					text: this.$t('form_error.password_not_valid'),
				}
			}
		},

		checkCGU() {
			this.errors.cgu = { valid: true, text: '' }
			if (!this.form.cgu) {
				this.errors.cgu = {
					valid: false,
					text: this.$t('form_error.cgu_empty'),
				}
			}
		},

		checkFirstname() {
			this.errors.first_name = { valid: true, text: '' }
			if (this.form.first_name.length === 0) {
				this.errors.first_name = {
					valid: false,
					text: this.$t('form_error.first_name_empty'),
				}
			}
		},

		checkFormValid() {
			this.checkFirstname()
			this.checkPassword()
			this.checkEmail()
			this.checkCGU()
			return Object.values(this.errors).every((field) => field.valid)
		},

		isMobile() {
			const toMatch = [
				/Android/i,
				/webOS/i,
				/iPhone/i,
				/iPad/i,
				/iPod/i,
				/BlackBerry/i,
				/Windows Phone/i,
			]

			return toMatch.some((toMatchItem) => {
				return navigator.userAgent.match(toMatchItem)
			})
		},

		submitForm() {
			this.isLoading = true
			if (!this.checkFormValid()) {
				this.isLoading = false
				return
			}
			this.$store.dispatch('mixpanel/trackAction', {
				mixpanel: this.$mixpanel,
				actionName: 'click-signup',
				data: {
					email: this.form.email,
				},
			})

			// Name cannot be empty when creating company
			const domain = this.form.email.substring(this.form.email.lastIndexOf('@') + 1)
			this.form.name = domain.substring(0, domain.lastIndexOf('.')).substring()
			this.form.first_name = this.form.email.substring(0, this.form.email.lastIndexOf('@'))

			this.form.first_name =
				this.form.first_name[0].toUpperCase() + this.form.first_name.slice(1)
			this.form.complete_onboarding = false
			this.form.plan = 2
			if (this.isMobile()) {
				this.form.is_mobile = true
			}
			this.form.language = this.$i18n.locale
			this.$store
				.dispatch('company/create', { data: this.form })
				.then((res) => {
					this.isLoading = false
					window.dataLayer.push({
						event: 'screenChange',
						event_category: 'sign_up',
						event_action: 'next',
						event_label: 'step_sign-up',
					})
					if (!this.isMobile()) {
						this.$router.push(
							`/sign-up/onboarding/website?token=${res.onboarding_token}&id=${res.id}`,
						)
					} else {
						window.dataLayer.push({
							event: 'screenChange',
							event_category: 'sign_up',
							event_action: 'next',
							event_label: 'step_sign-up',
						})
						this.$emit('signUpMobile')
					}
				})
				.catch((err) => {
					if (!err.response?.data) {
						this.isLoading = false
						return
					}

					Object.keys(err.response.data).forEach((key) => {
						if (key === 'email') {
							if (err.response.data[key][0] === 'Email already exists') {
								this.errors.email = {
									valid: false,
									text: this.$t('form_error.signup_form_error'),
								}
							}
						}
					})
					this.isLoading = false
				})
		},
	},
}
</script>

<style scoped>
.loader {
	margin: auto;
}
.sign-up-container {
	max-width: 460px;
	display: flex;
	flex-direction: column;
	padding-top: 30px;
}
.container-security-rules {
	margin-bottom: 18px;
}
.security-rules {
	font-style: normal;
	font-weight: 500;
	font-size: 10px;
	line-height: 20px;
	margin: 0px;
	display: flex;
	flex-direction: row;
	color: #ccd2e3;
}
.security-rules > span {
	display: flex;
	align-items: center;
}
.security-rules > span > img {
	margin-right: 5px;
}

.rule-valid {
	color: #00cc99;
}

.submit {
	margin: 10px auto auto;
}

.checkbox-container,
.error-message {
	font-weight: 400;
	font-size: 10px;
	line-height: 12px;
	color: #000000;
	display: flex;
	align-items: center;
}
.checkbox-container > label {
	margin-left: 10px;
}
.submit:hover {
	opacity: 0.9;
}

.allready-account {
	font-weight: 400;
	font-size: 10px;
	line-height: 12px;
	margin: 10px auto 0px auto;
}

.field-error {
	border: 1px solid red !important;
}

.error-message {
	font-size: 12px;
	color: red;
	margin-left: auto;
	margin-top: 5px;
}

.cgv-cgu-link {
	text-decoration-line: underline;
	color: #000000;
}

.login-link {
	text-decoration-line: underline;
	color: var(--primary-button-bg);
	font-weight: 700;
	font-size: 10px;
	line-height: 12px;
}
.require {
	color: #b2b2b2;
}

.container-error {
	display: flex;
	width: inherit;
}

input[type='checkbox'] {
	-moz-appearance: none;
	-webkit-appearance: none;
	-o-appearance: none;
	width: 15px;
	height: 15px;
	border: 1px black;
	background: #ffffff;
	border: 2px solid var(--primary-button-bg);
	border-radius: 4px;
	display: grid;
	place-content: center;
}

input[type='checkbox']::before {
	content: '';
	width: 0.65em;
	height: 0.65em;
	transform: scale(0);
	transition: 120ms transform ease-in-out;
	box-shadow: inset 1em 1em var(--primary-button-bg);
}

input[type='checkbox']:checked::before {
	transform: scale(1);
}

.security-rules {
	min-width: 150px;
}

.security-rules > span {
	min-width: 150px;
}
.security-rules > span:nth-child(2) {
	margin-left: auto;
}
</style>
